import type { RolloutPreviewInput, RolloutPreviewResult, WaveDossier, WaveReadiness } from '@/api/waves'
import { RolloutEditor } from './RolloutEditor'

// Rein synthetische Daten. Diese Kennungen werden nie an die API übergeben.
const scopeHash = 'demo-scope-nur-lokal'
const dossierHash = 'demo-dossier-nur-lokal'
const demoWaveId = 777777777

export const demoReadiness: WaveReadiness = {
  checkedAt: new Date().toISOString(), waveId: demoWaveId, profileId: demoWaveId,
  scopeHash, dossierHash, previewReady: true, liveReady: false,
  checks: [
    { key: 'Demo-Coverage', category: 'preview', status: 'pass', reasonCode: 'demo_only', nextAction: 'Keine Aktion: synthetisches Beispiel.' },
    { key: 'Live-Freigabe', category: 'live', status: 'blocked', reasonCode: 'demo_only', nextAction: 'Diese lokale Demo kann keine Live-Freigabe erteilen.' },
  ],
  members: [{ asin: 'DEMO-ASIN-1', sellerSku: 'DEMO-SKU-1' }],
}

export const demoDossier: WaveDossier = {
  generatedAt: new Date().toISOString(), targetAcos: 0.3,
  coverage: { mappedChildren: 1, campaignsInWave: 1 },
  queries: [{
    term: 'beispiel regal', tier: 'A', sourceCampaignId: demoWaveId, startBid: 0.42,
    kpis: { impressions: 100, clicks: 10, spend: 5, sales: 20, orders: 1, acos: 0.25, cpcHist: 0.5, cvr: 0.1 },
    economics: { cvr: 0.1, cpc_rule_bid: 0.55, legacy_aov_cap: 0.6, stahlia_margin_cap: 0.42, max_bid: 0.42, target_bid: 0.42, blocked: false, block_reasons: [], deviation_pct: -0.3, deviation_reason: null },
  }],
  economics: {
    economics_mode: 'demo_lokal', eligible_for_arming: false, version: 1, scope_hash: scopeHash, dossier_hash: dossierHash,
    built_at: new Date().toISOString(), confirmed_margin_net: 4.2, usable: true, block_reasons: [],
    checked_at: new Date().toISOString(), expires_at: new Date(Date.now() + 86400000).toISOString(), data_age_seconds: 0,
    sources: { provider: { enabled: true, contract_version: null, account: null, seller_id: null, marketplace_id: null, validity_basis: null, queried_at: null, requested_skus: 1, returned_rows: 1, confirmed_rows: 1, error_code: null, disabled_reasons: [] }, legacy: { aov: 20, mature_cutoff: '', source: 'demo_lokal' } },
  },
}

export async function demoPreview(waveId: number, input: RolloutPreviewInput, signal?: AbortSignal): Promise<RolloutPreviewResult> {
  if (signal?.aborted) throw new DOMException('Abgebrochen', 'AbortError')
  if (waveId !== demoWaveId || input.scopeHash !== scopeHash || input.dossierHash !== dossierHash || !input.groups.length || input.groups.some((group) =>
    group.members?.length !== 1 || group.members[0]?.asin !== 'DEMO-ASIN-1' || group.members[0]?.sku !== 'DEMO-SKU-1' ||
    group.queries?.length !== 1 || group.queries[0]?.sourceCampaignId !== demoWaveId || group.queries[0]?.term !== 'beispiel regal' ||
    !['ZUGPFERDE', 'WACHSTUMSPOOL', 'SAMMELBECKEN'].includes(group.class ?? '') || !group.campaignName || !group.adGroupName || !group.dailyBudgetEur
  )) throw new Error('Nur lokale Demo-Fixture zulässig')
  const preview: RolloutPreviewResult = {
    version: 1, scopeHash, dossierHash, planHash: 'demo-plan-nur-lokal', operationKey: 'demo-kein-serverauftrag',
    previewOnly: true, executable: false, blockers: [],
    drafts: input.groups.map((group) => ({
      clientRef: group.clientRef, class: group.class!, members: [{ asin: 'DEMO-ASIN-1', sku: 'DEMO-SKU-1' }], operationKey: 'demo-kein-serverauftrag',
      campaign: { clientRef: `demo-kampagne-${group.clientRef}`, adType: 'SP', name: group.campaignName!, state: 'PAUSED', targetingType: 'manual', budgetType: 'daily', dailyBudgetEur: group.dailyBudgetEur! },
      adGroup: { clientRef: `demo-gruppe-${group.clientRef}`, campaignClientRef: `demo-kampagne-${group.clientRef}`, name: group.adGroupName!, state: 'PAUSED' },
      keywords: group.queries!.map((query, index) => ({
        clientRef: `demo-query-${group.clientRef}-${index}`, adGroupClientRef: `demo-gruppe-${group.clientRef}`, term: query.term,
        matchType: 'EXACT', state: 'PAUSED', tier: 'A', source: query, bidEur: 0.42,
        economics: { maxBidEur: 0.42, cpcRuleBidEur: 0.55, legacyAovCapEur: 0.6, marginCapEur: 0.42 },
      })),
    })),
  }
  return preview
}

export function DemoWaveEconomics() {
  return <div className="space-y-4" data-testid="wave-demo">
    <div className="rounded-md border border-amber-500 p-4">
      <h1 className="font-semibold">Lokales Demo-Beispiel: Wellen-Economics</h1>
      <p>Alle Daten sind erfunden. Diese Ansicht sendet keine API-Anfragen und erteilt keine Serverfreigabe.</p>
    </div>
    <div className="rounded-md border border-[var(--border)] p-4 text-sm">
      <p>Readiness: Vorschau bereit · Live blockiert · nächster Schritt: keine Aktion, nur Beispiel.</p>
      <p>Coverage: 1 von 1 Beispiel-Mitgliedern · 1 Beispiel-Kampagne</p>
      <p>Datenalter: 0 Sekunden · Margen-Cap: 0,42 €/Klick · Sellerboard-AOV-Vergleich (optional): 0,60 €/Klick</p>
    </div>
    <RolloutEditor waveId={demoWaveId} readiness={demoReadiness} dossier={demoDossier} previewRequest={demoPreview} />
  </div>
}
